import React, { useEffect, useRef } from "react";
import { getVimeoEmbedUrl, loadVimeoPlayer } from "../utils/vimeo";

const VimeoPlayer = ({ video, title, autoplay = false, onEnd }) => {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const onEndRef = useRef(onEnd);
  const src = getVimeoEmbedUrl(video, autoplay);

  useEffect(() => {
    onEndRef.current = onEnd;
  }, [onEnd]);

  useEffect(() => {
    const iframe = iframeRef.current;
    let cancelled = false;
    let player;
    let completed = false;
    const handleEnd = () => {
      if (!cancelled && !completed) {
        completed = true;
        onEndRef.current?.();
      }
    };

    // The iframe starts loading before the SDK download. The SDK only adds
    // completion tracking, and attaching to the iframe avoids an oEmbed lookup.
    loadVimeoPlayer().then((Player) => {
      if (cancelled) {
        return;
      }

      player = new Player(iframe);
      playerRef.current = player;
      player.on("ended", handleEnd);

      // A slow SDK download could finish after the video has already ended.
      player.getEnded().then((ended) => {
        if (ended) {
          handleEnd();
        }
      }).catch(() => {
        // Keep listening for completion if the initial status check fails.
      });
    }).catch((error) => {
      if (!cancelled) {
        console.error("Unable to load Vimeo completion tracking.", error);
      }
    });

    return () => {
      cancelled = true;

      if (!player) {
        return;
      }

      player.off("ended", handleEnd);
      playerRef.current = null;

      // destroy() removes the iframe. Wait for React's DOM cleanup, and allow
      // StrictMode's immediate effect restart to reuse the same player.
      Promise.resolve().then(() => {
        if (playerRef.current !== player) {
          return player.destroy();
        }
      }).catch((error) => {
        console.error("Unable to clean up Vimeo player.", error);
      });
    };
  }, [src]);

  return (
    <div id="vimeoFrame">
      <iframe
        key={src}
        ref={iframeRef}
        src={src}
        title={title || "Vimeo video"}
        width="640"
        height="360"
        style={{ border: 0 }}
        loading="eager"
        allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
        allowFullScreen
      />
    </div>
  );
};

export default VimeoPlayer;
